import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../index";
import "./MetagameBreakdown.css";
import { aggregateCardStatistics } from "./functions/aggregateCardStatistics";

const CACHE_KEY = "cardListCache";
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

const fetchCardData = async () => {
  const cachedData = localStorage.getItem(CACHE_KEY);
  if (cachedData) {
    const { timestamp, data } = JSON.parse(cachedData);
    if (Date.now() - timestamp < CACHE_TTL) {
      return data;
    }
  }

  const cardCollection = collection(db, "cards");
  const cardSnapshot = await getDocs(cardCollection);
  const cardList = cardSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ timestamp: Date.now(), data: cardList })
  );

  return cardList;
};

const calculateDeckCost = (deck, cardCostMap) => {
  const extractCardId = (key) => key.split("_")[0];

  let totalCost = 0;

  if (Array.isArray(deck.main)) {
    deck.main.forEach((cardEntry) => {
      const [rawCardId, quantity] = Object.entries(cardEntry)[0];
      const cardId = extractCardId(rawCardId);
      totalCost += (cardCostMap[cardId] || 0) * quantity;
    });
  }

  if (Array.isArray(deck.cheers)) {
    deck.cheers.forEach((cardEntry) => {
      const [rawCardId, quantity] = Object.entries(cardEntry)[0];
      const cardId = extractCardId(rawCardId);
      totalCost += (cardCostMap[cardId] || 0) * quantity;
    });
  }

  if (deck.oshi) {
    const cardId = extractCardId(deck.oshi);
    totalCost += cardCostMap[cardId] || 0;
  }

  return totalCost;
};

const processMetaData = (tournaments, cards, filterDate = null) => {
  const cardCostMap = {};
  const cardImageMap = {};

  cards.forEach((card) => {
    const cardId = card.id;
    const combinedId = `${card.id}_${card.rarity}`;
    cardCostMap[cardId] = parseFloat(card.price) || 0;
    cardImageMap[combinedId] = `/images/${combinedId}.png`;
  });

  const archetypeToCardMap = {
    "Mumei Promise": "hBP01-002_OSR",
    "Mono Kanata": "hBP01-009_C",
    "Kanata Promise": "hBP01-001_OSR",
    "Pekora Promise": "hBP01-004_OSR",
    "Mono Azki": "hBP01-047_RR",
    "Sora Promise": "hBP01-023_RR",
    "Moona Zeta": "hBP01-008_OSR",
    "Red White Promise": "hBP01-075_R",
    "12 Fan Polka": "hBP01-006_OSR",
    "Sora Azki": "hSD01-002_OSR",
    "8 Fan Polka": "hBP01-071_RR",
    "Kanata SorAZ": "hSD01-013_R",
    "Kiara Lui Iroha": "hBP01-066_R",
    "Kanata Sora": "hSD01-004_R",
    "Suisei Iroha": "hBP01-007_OSR",
    "Reine Iofi": "hBP02-023_UR",
  };

  const archetypeData = {};
  let totalDecks = 0;

  const parseDateFromYYYYMMDD = (dateString) => {
    const year = parseInt(dateString.slice(0, 4), 10);
    const month = parseInt(dateString.slice(4, 6), 10) - 1;
    const day = parseInt(dateString.slice(6, 8), 10);
    return new Date(year, month, day);
  };

  const filteredTournaments = filterDate
    ? tournaments.filter(
        (tournament) => parseDateFromYYYYMMDD(tournament.date) >= filterDate
      )
    : tournaments;

  filteredTournaments.forEach((tournament) => {
    if (!Array.isArray(tournament.decklists)) return;

    tournament.decklists.forEach((deck) => {
      totalDecks++;
      const archetype = deck.archetype || "Other";
      const deckCost = calculateDeckCost(deck, cardCostMap);

      if (!archetypeData[archetype]) {
        const mappedCard = archetypeToCardMap[archetype];
        const imageKey = mappedCard ? cardImageMap[mappedCard] : null;
        archetypeData[archetype] = {
          count: 0,
          totalCost: 0,
          oshiImage: imageKey || "/images/default.png",
        };
      }

      archetypeData[archetype].count++;
      archetypeData[archetype].totalCost += deckCost;
    });
  });

  const metaArray = Object.entries(archetypeData).map(([archetype, data]) => ({
    archetype,
    count: data.count,
    metaPercentage: ((data.count / totalDecks) * 100).toFixed(1),
    averageCost: (data.totalCost / data.count).toFixed(2),
    oshiImage: data.oshiImage,
  }));

  metaArray.sort((a, b) => {
    if (a.archetype === "Other") return 1; // Push "Other" decks to the end
    if (b.archetype === "Other") return -1;
    return b.metaPercentage - a.metaPercentage;
  });

  return metaArray;
};

const MetagameBreakdown = () => {
  const [metaData, setMetaData] = useState([]);
  const [topCards, setTopCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("30days");

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const tournaments = await fetch("/tournaments.json").then((res) =>
          res.json()
        );
        const cards = await fetchCardData();
    
        // Calculate the filter date based on the selected filter option (7days, 30days, 90days)
        const filterDate =
          filter === "7days"
            ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
            : filter === "30days"
            ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            : filter === "90days"
            ? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
            : null;
    
        // Fetch meta data for tournaments, cards, and filtered by date range
        const metaArray = processMetaData(tournaments, cards, filterDate);
        setMetaData(metaArray);
    
        // Aggregate card statistics based on tournaments and cards
        const cardStats = aggregateCardStatistics(
          tournaments,
          cards,
          null, // No archetype filter for top cards
          filterDate // Use the date filter to filter top cards
        );
    
        // Filter the cards into different categories (holomem and support)
        const holomemCards = cardStats.main
          .filter(
            (card) =>
              card.type &&
              card.type.toLowerCase().includes("holomem") &&
              !card.type.toLowerCase().includes("debut")
          )
          .slice(0, 10);
    
        const supportCards = cardStats.main
          .filter(
            (card) => card.type && card.type.toLowerCase().includes("support")
          )
          .slice(0, 10);
    
        // Prepare the topCards object with categories for top cards (oshi, holomem, support)
        setTopCards({
          oshi: cardStats.oshi.slice(0, 10),
          holomem: holomemCards,
          support: supportCards,
        });
      } catch (error) {
        console.error("Error fetching meta data:", error);
      } finally {
        setLoading(false);
      }
    };    

    fetchMetaData();
  }, [filter]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="metagame-breakdown">
      <h1>Metagame Breakdown</h1>

      <div className="filter-dropdown">
        <label htmlFor="date-filter">Filter by:</label>
        <select
          id="date-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All Time</option>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="90days">Last 90 Days</option>
        </select>
      </div>

      <div className="archetype-grid">
        {metaData.map((meta, index) => (
          <Link
            to={`/deck-view/${encodeURIComponent(meta.archetype)}`}
            key={index}
            className="archetype-card-link"
          >
            <div className="archetype-card">
              <img
                src={meta.oshiImage}
                alt={`${meta.archetype} Oshi`}
                className="archetype-oshi-image"
              />
              <h2 className="archetype-name">{meta.archetype}</h2>
              <p className="archetype-meta">
                {meta.metaPercentage}% ({meta.count} decks)
              </p>
              <p className="archetype-cost">
                Average Cost: ${meta.averageCost}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {topCards && (
        <div className="top-cards-section">
          <h2>Top Cards</h2>
          <div className="top-cards-categories">
            {["oshi", "holomem", "support"].map((category) => (
              <div key={category} className="top-cards-category">
                <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                <ul>
                  {topCards[category].map((card) => (
                    <li key={card.id} className="top-card-item">
                      <Link
                        to={`/card/${card.id}_${card.rarity}`}
                        className="top-card-link"
                      >
                        <span className="top-card-name">{card.name}</span>
                      </Link>
                      <div className="top-card-details">
                        <span className="top-card-type">{card.type}</span>
                        <div className="top-card-stats">
                          <span className="top-card-meta">
                            {card.percentage}% of decks
                          </span>
                          <span className="top-card-average">
                            Avg copies: {card.average}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetagameBreakdown;
